import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "children", "level", "drawingStyle", "colorTheme", "size", "disabled", "startIcon", "endIcon", "loading", "loadingPosition", "type", "onClick", "labelHidden", "tabIndex", "data-testid"];
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { ThemeProvider } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { visuallyHidden } from '@mui/utils';
import { EmbersLoading } from '../loading/loading';
import { useId } from '../utils/useId';
import { BUTTON_ROOT_PREFIX, BUTTON_ICON_SMALL, BUTTON_ICON_NORMAL, buttonTheme } from './buttonStyles';
// eslint-disable-next-line react/display-name
export var EmbersButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var className = props.className,
    children = props.children,
    _props$level = props.level,
    level = _props$level === void 0 ? 'primary' : _props$level,
    _props$drawingStyle = props.drawingStyle,
    drawingStyle = _props$drawingStyle === void 0 ? 'filled' : _props$drawingStyle,
    _props$colorTheme = props.colorTheme,
    colorTheme = _props$colorTheme === void 0 ? 'purple' : _props$colorTheme,
    _props$size = props.size,
    size = _props$size === void 0 ? 'medium' : _props$size,
    disabled = props.disabled,
    startIcon = props.startIcon,
    endIcon = props.endIcon,
    loading = props.loading,
    _props$loadingPositio = props.loadingPosition,
    loadingPosition = _props$loadingPositio === void 0 ? 'start' : _props$loadingPositio,
    type = props.type,
    onClick = props.onClick,
    labelHidden = props.labelHidden,
    tabIndex = props.tabIndex,
    dataTestId = props['data-testid'],
    otherProps = _objectWithoutProperties(props, _excluded);
  var intl = useIntl();
  var loadingIndicator = /*#__PURE__*/React.createElement(EmbersLoading, {
    "aria-hidden": true,
    size: size === 'small' ? BUTTON_ICON_SMALL : BUTTON_ICON_NORMAL
  });
  var showStartLoadingIndicator = loading && loadingPosition === 'start';
  var showCenterLoadingIndicator = loading && loadingPosition === 'center';

  // startIcon
  var buttonStartIcon = startIcon;
  if (!startIcon && showStartLoadingIndicator) {
    buttonStartIcon = loadingIndicator;
  }

  // validation
  var validatedLevel = level;
  if (drawingStyle === 'text') {
    validatedLevel = 'tertiary';
  }

  // MUI uses "variant" for a slightly different schema
  var muiVariant;
  if (drawingStyle === 'text') {
    muiVariant = 'text';
  } else if (drawingStyle === 'outlined') {
    muiVariant = 'outlined';
  } else {
    // both "filled" and "tonal" are "contained" for mui's purposes
    muiVariant = 'contained';
  }

  // MUI uses "color" for a slightly different schema
  var muiType;
  if (level === 'tertiary') {
    muiType = 'info';
  } else if (level === 'secondary') {
    muiType = 'secondary';
  } else {
    muiType = 'primary';
  }
  var _useState = useState(void 0),
    _useState2 = _slicedToArray(_useState, 2),
    ariaLabel = _useState2[0],
    setAriaLabel = _useState2[1];
  var _useState3 = useState(children),
    _useState4 = _slicedToArray(_useState3, 2),
    buttonText = _useState4[0],
    setButtonText = _useState4[1];
  useEffect(function () {
    if (labelHidden) {
      // prevents readout of icon and reads hidden label when icon only
      setAriaLabel(children);
      setButtonText(void 0);
    } else {
      if (!loading) {
        setAriaLabel(void 0); // From the loading state to the non-loading state， remove aria-label
        setButtonText(children);
      } else if (showStartLoadingIndicator) {
        setAriaLabel(children);
        setButtonText(children);
      } else if (showCenterLoadingIndicator) {
        // when loading = true, the user may change the button text, like "Loading..."
        // when loadingPosition = center, the button text should be the same as the previous text to keep the button width，and readout the new text
        setAriaLabel(children);
      }
    }
  }, [children, labelHidden]);
  var defaultAriaProps = {
    'aria-label': ariaLabel,
    'aria-busy': loading ? 'true' : 'false'
  };
  var labelledById = useId();
  if (loading) {
    // In macOS, it can't be read with aria-label, aria-labelledby is fine
    delete defaultAriaProps['aria-label'];
    defaultAriaProps['aria-labelledby'] = labelledById;
  }
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: buttonTheme
  }, /*#__PURE__*/React.createElement(Button, _extends({
    ref: ref,
    tabIndex: disabled || loading ? -1 : tabIndex || 0,
    variant: muiVariant,
    disableRipple: true,
    size: size,
    color: muiType,
    disabled: disabled,
    startIcon: buttonStartIcon,
    endIcon: endIcon,
    className: classnames(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, "".concat(BUTTON_ROOT_PREFIX, "-root"), true), "".concat(BUTTON_ROOT_PREFIX, "-hasStartIcon"), buttonStartIcon), "".concat(BUTTON_ROOT_PREFIX, "-hasEndIcon"), endIcon), "".concat(BUTTON_ROOT_PREFIX, "-").concat(validatedLevel, "Level"), true), "".concat(BUTTON_ROOT_PREFIX, "-").concat(colorTheme, "Color"), true), "".concat(BUTTON_ROOT_PREFIX, "-").concat(drawingStyle, "DrawingStyle"), true), "".concat(BUTTON_ROOT_PREFIX, "-icon-only"), labelHidden || !children), "".concat(BUTTON_ROOT_PREFIX, "-loading"), loading), "".concat(BUTTON_ROOT_PREFIX, "-loading-start"), showStartLoadingIndicator), "".concat(BUTTON_ROOT_PREFIX, "-loading-center"), showCenterLoadingIndicator), className),
    type: loading ? 'button' : type,
    onClick: !loading ? onClick : void 0,
    "data-testid": dataTestId
  }, defaultAriaProps, otherProps), showCenterLoadingIndicator ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: classnames("".concat(BUTTON_ROOT_PREFIX, "-loadingIndicator"), "".concat(BUTTON_ROOT_PREFIX, "-loadingIndicator-center"))
  }, loadingIndicator), /*#__PURE__*/React.createElement("span", null, buttonText)) : buttonText, loading ? /*#__PURE__*/React.createElement("span", {
    id: labelledById,
    style: visuallyHidden
  }, ariaLabel || intl.formatMessage({
    id: 'embers.button.loading',
    defaultMessage: 'Loading'
  })) : /*#__PURE__*/React.createElement(React.Fragment, null)));
});